import { z } from "zod";
import { auditActorFields } from "../common/audit.js";
import { pagedResponse, paginationQuerySchema } from "../common/pagination.js";
import { booleanQueryParam } from "../common/query-params.js";
export const planogramStatusEnum = z.enum([
    "draft",
    "approved",
    "active",
    "archived",
    "retired",
]);
// — planograms —
export const planogramInfo = z.object({
    id: z.string().uuid(),
    categoryId: z.string().uuid().nullable(),
    categoryName: z.string().nullable(),
    name: z.string(),
    code: z.string(),
    version: z.number().int().positive(),
    status: planogramStatusEnum,
    effectiveFrom: z.string().datetime().nullable(),
    effectiveTo: z.string().datetime().nullable(),
    sourceFileUri: z.string().nullable(),
    isActive: z.boolean(),
    approvedAt: z.string().datetime().nullable(),
    positionsCount: z.number().int(),
    assignmentsCount: z.number().int(),
    metadata: z.record(z.unknown()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    ...auditActorFields,
});
export const createPlanogramRequest = z.object({
    categoryId: z.string().uuid().nullish(),
    name: z.string().min(1).max(255),
    code: z.string().min(1).max(100),
    version: z.number().int().positive().default(1),
    status: planogramStatusEnum.default("draft"),
    effectiveFrom: z.string().datetime().nullish(),
    effectiveTo: z.string().datetime().nullish(),
    sourceFileUri: z.string().nullish(),
    isActive: z.boolean().default(true),
    metadata: z.record(z.unknown()).default({}),
});
export const updatePlanogramRequest = createPlanogramRequest
    .partial()
    .refine((v) => Object.keys(v).length > 0, {
    message: "At least one field required",
});
export const listPlanogramsQuery = paginationQuerySchema.extend({
    categoryId: z.string().uuid().optional(),
    status: planogramStatusEnum.optional(),
    isActive: booleanQueryParam.optional(),
});
export const planogramsListResponse = pagedResponse(planogramInfo);
export const archivePlanogramResponse = z.object({ ok: z.literal(true) });
// — positions (sub-resource) —
export const planogramPositionInfo = z.object({
    id: z.string().uuid(),
    planogramId: z.string().uuid(),
    productId: z.string().uuid(),
    productName: z.string(),
    productBrand: z.string().nullable(),
    shelfLevel: z.number().int().nullable(),
    positionRank: z.number().int().nullable(),
    xStartPct: z.number().nullable(),
    xEndPct: z.number().nullable(),
    xOffsetMm: z.number().int().nullable(),
    slotWidthMm: z.number().int().nullable(),
    expectedFacings: z.number().int(),
    minFacings: z.number().int().nullable(),
    maxFacings: z.number().int().nullable(),
    horizontalFacing: z.number().int().nullable(),
    verticalFacing: z.number().int().nullable(),
    depthFacing: z.number().int().nullable(),
    expectedQuantity: z.number().nullable(),
    adjacencyGroup: z.string().nullable(),
    placementRule: z.record(z.unknown()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const createPlanogramPositionRequest = z.object({
    productId: z.string().uuid(),
    shelfLevel: z.number().int().nullish(),
    positionRank: z.number().int().nullish(),
    xStartPct: z.number().min(0).max(100).nullish(),
    xEndPct: z.number().min(0).max(100).nullish(),
    xOffsetMm: z.number().int().nullish(),
    slotWidthMm: z.number().int().positive().nullish(),
    expectedFacings: z.number().int().min(1).default(1),
    minFacings: z.number().int().min(0).nullish(),
    maxFacings: z.number().int().min(0).nullish(),
    horizontalFacing: z.number().int().nullish(),
    verticalFacing: z.number().int().nullish(),
    depthFacing: z.number().int().nullish(),
    expectedQuantity: z.number().nullish(),
    adjacencyGroup: z.string().max(100).nullish(),
    placementRule: z.record(z.unknown()).default({}),
});
export const updatePlanogramPositionRequest = createPlanogramPositionRequest
    .partial()
    .refine((v) => Object.keys(v).length > 0, {
    message: "At least one field required",
});
export const planogramPositionsListResponse = z.object({
    items: z.array(planogramPositionInfo),
});
// — assignments —
export const planogramAssignmentInfo = z.object({
    id: z.string().uuid(),
    planogramId: z.string().uuid(),
    planogramName: z.string(),
    planogramCode: z.string(),
    storeId: z.string().uuid(),
    storeName: z.string(),
    storeCode: z.string(),
    zoneId: z.string().uuid().nullable(),
    zoneName: z.string().nullable(),
    fixtureId: z.string().uuid().nullable(),
    fixtureCode: z.string().nullable(),
    effectiveFrom: z.string().datetime().nullable(),
    effectiveTo: z.string().datetime().nullable(),
    isActive: z.boolean(),
    metadata: z.record(z.unknown()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    ...auditActorFields,
});
export const createPlanogramAssignmentRequest = z.object({
    planogramId: z.string().uuid(),
    storeId: z.string().uuid(),
    zoneId: z.string().uuid().nullish(),
    fixtureId: z.string().uuid().nullish(),
    effectiveFrom: z.string().datetime().nullish(),
    effectiveTo: z.string().datetime().nullish(),
    isActive: z.boolean().default(true),
    metadata: z.record(z.unknown()).default({}),
});
export const updatePlanogramAssignmentRequest = createPlanogramAssignmentRequest
    .partial()
    .refine((v) => Object.keys(v).length > 0, {
    message: "At least one field required",
});
export const listPlanogramAssignmentsQuery = paginationQuerySchema.extend({
    planogramId: z.string().uuid().optional(),
    storeId: z.string().uuid().optional(),
    zoneId: z.string().uuid().optional(),
    fixtureId: z.string().uuid().optional(),
    isActive: booleanQueryParam.optional(),
});
export const planogramAssignmentsListResponse = pagedResponse(planogramAssignmentInfo);
export const archivePlanogramAssignmentResponse = z.object({
    ok: z.literal(true),
});
//# sourceMappingURL=planograms.schemas.js.map